port module Main exposing (..)

import Browser
import Html exposing (..)
import Html.Attributes exposing (..)
import Html.Events exposing (..)
import Http
import Iso8601
import Json.Decode as JD
import Json.Encode as JE
import List.Extra exposing (groupsOf)
import QRHashValidator exposing (isValidQRHashFormat)
import RemoteData as RD exposing (RemoteData)
import Task
import Time
import Url


main : Program Flags Model Msg
main =
    Browser.element
        { init = init
        , update = update
        , subscriptions = subscriptions
        , view = view
        }



-- MODEL


type ApiError
    = ClientError Int String
    | ServerError Int String
    | DecodeError String
    | NetworkError
    | Timeout
    | BadUrl String


type alias ApiData a =
    RemoteData ApiError a


type alias Flags =
    { backendUrl : String
    , serviceUrl : String
    , qrSessionId : Maybe String
    }


type alias Config =
    { backendUrl : String
    , serviceUrl : String
    }


type alias UserInfo =
    { name : String
    , roles : List String
    }


type alias OneTimePassword =
    { otp : String
    , expiresAt : Time.Posix
    }


type alias QRSession =
    { id : String
    , isValid : Bool
    }


type alias Model =
    { currentTime : Time.Posix
    , zone : Time.Zone
    , config : Config
    , qrSession : Maybe QRSession
    , userInfo : ApiData UserInfo
    , oneTimePassword : ApiData OneTimePassword
    , logoutResponse : ApiData ()
    }


init : Flags -> ( Model, Cmd Msg )
init flags =
    let
        config =
            { backendUrl = flags.backendUrl
            , serviceUrl = flags.serviceUrl
            }

        qrSession =
            flags.qrSessionId |> Maybe.map (\qrSessionId -> { id = qrSessionId, isValid = isValidQRHashFormat qrSessionId })
    in
    ( { currentTime = Time.millisToPosix 0
      , zone = Time.utc
      , config = config
      , qrSession = qrSession
      , userInfo = RD.NotAsked
      , oneTimePassword = RD.NotAsked
      , logoutResponse = RD.NotAsked
      }
    , Cmd.batch
        [ Task.perform AdjustTimeZone Time.here
        , Task.perform Tick Time.now
        , getUserInfo config
        ]
    )


expectJsonWithErrorBody : (ApiData a -> msg) -> JD.Decoder a -> Http.Expect msg
expectJsonWithErrorBody toMsg decoder =
    let
        resolve response =
            case response of
                Http.GoodStatus_ _ body ->
                    case JD.decodeString decoder body of
                        Ok value ->
                            Ok value

                        Err err ->
                            Err (DecodeError (JD.errorToString err))

                Http.BadStatus_ metadata body ->
                    let
                        status =
                            metadata.statusCode
                    in
                    if status >= 500 then
                        Err (ServerError status body)

                    else
                        Err (ClientError status body)

                Http.BadUrl_ url ->
                    Err (BadUrl url)

                Http.Timeout_ ->
                    Err Timeout

                Http.NetworkError_ ->
                    Err NetworkError

        resultToRemoteData result =
            case result of
                Ok value ->
                    RD.Success value

                Err error ->
                    RD.Failure error
    in
    Http.expectStringResponse (resultToRemoteData >> toMsg) resolve


expectWhateverWithErrorBody : (ApiData () -> msg) -> Http.Expect msg
expectWhateverWithErrorBody toMsg =
    let
        resolve response =
            case response of
                Http.GoodStatus_ _ _ ->
                    Ok ()

                Http.BadStatus_ metadata body ->
                    let
                        status =
                            metadata.statusCode
                    in
                    if status >= 500 then
                        Err (ServerError status body)

                    else
                        Err (ClientError status body)

                Http.BadUrl_ url ->
                    Err (BadUrl url)

                Http.Timeout_ ->
                    Err Timeout

                Http.NetworkError_ ->
                    Err NetworkError

        resultToRemoteData result =
            case result of
                Ok value ->
                    RD.Success value

                Err error ->
                    RD.Failure error
    in
    Http.expectStringResponse (resultToRemoteData >> toMsg) resolve


getUserInfo : Config -> Cmd Msg
getUserInfo config =
    Http.riskyRequest
        { method = "get"
        , headers = []
        , url = config.serviceUrl ++ "/user/info"
        , body = Http.stringBody "application/json" ""
        , expect = expectJsonWithErrorBody GotUserInfo userInfoDecoder
        , timeout = Nothing
        , tracker = Nothing
        }


getOtp : Config -> String -> String -> Cmd Msg
getOtp config email qrSeshId =
    let
        httpBody =
            [ ( "email", JE.string email )
            , ( "qr_session_id", JE.string qrSeshId )
            ]
                |> JE.object
                |> Http.jsonBody
    in
    Http.riskyRequest
        { method = "post"
        , headers = []
        , url = config.serviceUrl ++ "/getotp"
        , body = httpBody
        , expect = expectJsonWithErrorBody GotOTP otpDecoder
        , timeout = Nothing
        , tracker = Nothing
        }


userInfoDecoder : JD.Decoder UserInfo
userInfoDecoder =
    JD.map2 UserInfo
        (JD.at [ "email" ] JD.string)
        (JD.at [ "roles" ] (JD.list JD.string))


otpDecoder : JD.Decoder OneTimePassword
otpDecoder =
    JD.map2 OneTimePassword
        (JD.at [ "otp" ] JD.string)
        (JD.at [ "expires_at" ] iso8601Decoder)


iso8601Decoder : JD.Decoder Time.Posix
iso8601Decoder =
    JD.string
        |> JD.andThen
            (\str ->
                case Iso8601.toTime str of
                    Ok time ->
                        JD.succeed time

                    Err _ ->
                        JD.fail ("Invalid ISO 8601 timestamp: " ++ str)
            )


logout : Config -> Cmd Msg
logout config =
    Http.riskyRequest
        { method = "post"
        , headers = []
        , url = config.serviceUrl ++ "/logout"
        , body = Http.stringBody "application/json" ""
        , expect = expectWhateverWithErrorBody GotLogoutResponse
        , timeout = Nothing
        , tracker = Nothing
        }



-- UPDATE


type Msg
    = Tick Time.Posix
    | AdjustTimeZone Time.Zone
    | NoOp
    | GotUserInfo (ApiData UserInfo)
    | ReceivedCurrentUrl String
    | GotOTP (ApiData OneTimePassword)
    | LogOut
    | GotLogoutResponse (ApiData ())


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    case msg of
        Tick newTime ->
            ( { model | currentTime = newTime }, Cmd.none )

        AdjustTimeZone newZone ->
            ( { model | zone = newZone }, Cmd.none )

        NoOp ->
            ( model, Cmd.none )

        GotUserInfo userInfoResponse ->
            case userInfoResponse of
                RD.Success userInfo ->
                    let
                        updatedModel =
                            { model | userInfo = userInfoResponse }
                    in
                    case isQrSessionPresentAndValid model.qrSession of
                        Just qrSession ->
                            ( { updatedModel | oneTimePassword = RD.Loading }, getOtp model.config userInfo.name qrSession.id )

                        Nothing ->
                            ( updatedModel, Cmd.none )

                _ ->
                    ( model
                    , getCurrentUrl ()
                    )

        ReceivedCurrentUrl currentUrl ->
            let
                encodedCurrentUrl : String
                encodedCurrentUrl =
                    Url.percentEncode currentUrl

                loginUrl : String
                loginUrl =
                    "https://login.genomicscore.be?uri=" ++ encodedCurrentUrl
            in
            ( model, redirectToLogin loginUrl )

        GotOTP otpResponse ->
            ( { model | oneTimePassword = otpResponse }, Cmd.none )

        LogOut ->
            ( { model | logoutResponse = RD.Loading }, logout model.config )

        GotLogoutResponse logoutResponse ->
            case logoutResponse of
                RD.Success () ->
                    ( model, getCurrentUrl () )

                RD.Failure _ ->
                    ( { model | logoutResponse = logoutResponse }, Cmd.none )

                _ ->
                    ( model, Cmd.none )


subscriptions : Model -> Sub Msg
subscriptions model =
    Sub.batch
        [ Time.every 1000 Tick
        , receiveCurrentUrl ReceivedCurrentUrl
        ]



-- VIEW


loading : Html Msg
loading =
    div
        [ attribute "aria-busy" "true"
        , style "margin" "2rem"
        ]
        [ text "Loading..." ]


view : Model -> Html Msg
view model =
    let
        groupToken : Int -> String -> String
        groupToken groupSize strToken =
            groupsOf groupSize (String.toList strToken)
                |> List.map (\group -> String.fromList group)
                |> String.join " "

        bodyAfterLogin =
            div []
                [ case model.oneTimePassword of
                    RD.Loading ->
                        loading

                    RD.NotAsked ->
                        text ""

                    RD.Failure err ->
                        viewApiError err

                    RD.Success otp ->
                        let
                            isExpired =
                                Time.posixToMillis model.currentTime >= Time.posixToMillis otp.expiresAt
                        in
                        if isExpired then
                            div [] [ p [] [ text "Expired. Scan a new QR code!" ] ]

                        else
                            div []
                                [ input
                                    [ style "font-size" "32px"
                                    , style "font-weight" "bold"
                                    , style "font-family" "ui-monospace"
                                    , style "text-align" "center"
                                    , value (groupToken 3 otp.otp)
                                    , readonly True
                                    ]
                                    []
                                , small [] [ text ("Expires in: " ++ displayRemainingTime otp.expiresAt model.currentTime) ]
                                ]
                ]
    in
    case model.userInfo of
        RD.NotAsked ->
            text ""

        RD.Loading ->
            loading

        RD.Failure err ->
            viewApiError err

        RD.Success _ ->
            div []
                [ h3 [] [ text "GC Authenticator" ]
                , case model.qrSession of
                    Just qrSession ->
                        if qrSession.isValid then
                            bodyAfterLogin

                        else
                            p [] [ text "Error: QR code is invalid!" ]

                    Nothing ->
                        p [] [ text "Error: no QR code provided!" ]
                , input [ type_ "button", class "secondary", onClick LogOut, value "Logout" ] []
                , case model.logoutResponse of
                    RD.Loading ->
                        loading

                    RD.Failure err ->
                        viewApiError err

                    _ ->
                        text ""
                ]


viewApiError : ApiError -> Html Msg
viewApiError error =
    div [ style "margin" "2rem" ] [ text (apiErrorToString error) ]


apiErrorToString : ApiError -> String
apiErrorToString error =
    case error of
        ClientError status body ->
            "Client error (" ++ String.fromInt status ++ "): " ++ body

        ServerError status body ->
            "Server error (" ++ String.fromInt status ++ "): " ++ body ++ " If this persists, please contact us through the helpdesk (https://help.genomicscore.be/helpdesk)"

        DecodeError err ->
            "Decode error: " ++ err

        NetworkError ->
            "Network error - please check your connection"

        Timeout ->
            "Request timed out"

        BadUrl url ->
            "Invalid URL: " ++ url


isQrSessionPresentAndValid : Maybe QRSession -> Maybe QRSession
isQrSessionPresentAndValid maybeQrSession =
    maybeQrSession
        |> Maybe.andThen
            (\qrSession ->
                if qrSession.isValid then
                    Just qrSession

                else
                    Nothing
            )


displayRemainingTime : Time.Posix -> Time.Posix -> String
displayRemainingTime expiresAt currentTime =
    let
        remainingTime =
            Time.posixToMillis expiresAt - Time.posixToMillis currentTime

        remainingMinutes =
            remainingTime // 60000

        remainingSeconds =
            (remainingTime // 1000) |> modBy 60
    in
    String.fromInt remainingMinutes ++ ":" ++ String.padLeft 2 '0' (String.fromInt remainingSeconds)


formatTime : Time.Zone -> Time.Posix -> String
formatTime zone time =
    let
        hour =
            String.fromInt (Time.toHour zone time) |> String.padLeft 2 '0'

        minute =
            String.fromInt (Time.toMinute zone time) |> String.padLeft 2 '0'

        second =
            String.fromInt (Time.toSecond zone time) |> String.padLeft 2 '0'
    in
    hour ++ ":" ++ minute ++ ":" ++ second



-- PORTS


port redirectToLogin : String -> Cmd msg


port getCurrentUrl : () -> Cmd msg


port receiveCurrentUrl : (String -> msg) -> Sub msg
